import React, { useCallback, useEffect, useRef, useState } from "react";
import { ptBR } from "date-fns/locale/pt-BR";
import { LuCalendarDays } from "react-icons/lu";
import {
  Calendar,
  InfoTooltip,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  TooltipProvider,
} from "@geti/identidade-visual-react";
import { formatDateFromJsDate, optionsType } from "../../../Components/Helper";

const SEARCH_DEBOUNCE_MS = 500;

interface SearchComponentsProps {
  onFiltersChange?: (payload: Record<string, unknown> | null) => void;
  embedded?: boolean;
}

const WORD_SEARCH_MODES = [
  { value: "exact", label: "Todas as palavras" },
  { value: "contain", label: "Contém palavras" },
] as const;

type WordSearchMode = (typeof WORD_SEARCH_MODES)[number]["value"];

const ALL_POST_TYPES = "Todos";
const POST_TYPE_PLACEHOLDER = "Filtrar por tipos";

function getPostTypeTriggerLabel(postType: string) {
  return postType === ALL_POST_TYPES ? POST_TYPE_PLACEHOLDER : postType;
}

function getWordSearchModeLabel(mode: WordSearchMode) {
  return (
    WORD_SEARCH_MODES.find((item) => item.value === mode)?.label ?? "Modo de busca"
  );
}

function formatDisplayDate(date: Date | undefined) {
  if (!date) return "";
  return date.toLocaleDateString("pt-BR");
}

function parseKeywords(value: string) {
  return value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.toLowerCase());
}

function DateField({
  placeholder,
  value,
  open,
  onOpenChange,
  onSelect,
}: {
  placeholder: string;
  value: Date | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (date: Date | undefined) => void;
}) {
  const handleSelect = (date: Date | undefined) => {
    onSelect(date);
    onOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger
        type="button"
        className="flex h-8 w-full min-w-0 cursor-pointer items-center justify-start gap-2 rounded-lg border border-input bg-transparent px-2.5 text-sm font-normal text-muted-foreground outline-none transition-colors dark:bg-input/30 dark:hover:bg-input/50 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        <LuCalendarDays className="size-4 shrink-0" aria-hidden />
        <span className="truncate">
          {value ? formatDisplayDate(value) : placeholder}
        </span>
      </PopoverTrigger>
      <PopoverContent align="start" side="bottom" className="z-[100]">
        <Calendar
          mode="single"
          selected={value}
          onSelect={handleSelect}
          locale={ptBR}
          defaultMonth={value ?? new Date()}
          captionLayout="dropdown"
          reverseYears
        />
      </PopoverContent>
    </Popover>
  );
}

const SearchComponents: React.FC<SearchComponentsProps> = ({
  onFiltersChange,
  embedded = false,
}) => {
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [dateFromOpen, setDateFromOpen] = useState(false);
  const [dateToOpen, setDateToOpen] = useState(false);
  const [wordSearchMode, setWordSearchMode] =
    useState<WordSearchMode>("exact");
  const [form, setForm] = useState({
    post_type: ALL_POST_TYPES,
    post_code: "",
    keywords: "",
  });
  const skipNextAutoSearch = useRef(true);

  const getWordFlags = (mode: WordSearchMode) => ({
    exact_words: mode === "exact",
    words_contain: mode === "contain",
  });

  const isFiltersEmpty = useCallback(
    () =>
      !dateFrom &&
      !dateTo &&
      (!form.post_type || form.post_type === ALL_POST_TYPES) &&
      !form.post_code.trim() &&
      !form.keywords.trim(),
    [dateFrom, dateTo, form.post_type, form.post_code, form.keywords]
  );

  const buildPayload = useCallback(
    (wordsOverride: string[]) => {
      const flags = getWordFlags(wordSearchMode);
      return {
        post_type:
          form.post_type && form.post_type !== ALL_POST_TYPES
            ? [form.post_type]
            : [],
        post_code: form.post_code,
        words: wordsOverride,
        exact_words: flags.exact_words,
        words_contain: flags.words_contain,
        date: dateFrom
          ? {
              from: formatDateFromJsDate(dateFrom),
              to: dateTo
                ? formatDateFromJsDate(dateTo)
                : formatDateFromJsDate(dateFrom),
            }
          : { from: null, to: null },
      };
    },
    [dateFrom, dateTo, form.post_type, form.post_code, wordSearchMode]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (skipNextAutoSearch.current) {
        skipNextAutoSearch.current = false;
        return;
      }

      if (isFiltersEmpty()) {
        onFiltersChange?.(null);
        return;
      }

      onFiltersChange?.(buildPayload(parseKeywords(form.keywords)));
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [
    dateFrom,
    dateTo,
    form,
    wordSearchMode,
    isFiltersEmpty,
    buildPayload,
    onFiltersChange,
  ]);

  return (
    <TooltipProvider>
      <div className="w-full min-w-0 p-1">
        {!embedded && (
          <h2 className="mb-3 text-lg font-semibold text-foreground">
            Pesquisar edições
          </h2>
        )}

        <div className="grid w-full min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <DateField
            placeholder="Data inicial"
            value={dateFrom}
            open={dateFromOpen}
            onOpenChange={setDateFromOpen}
            onSelect={setDateFrom}
          />

          <DateField
            placeholder="Data final"
            value={dateTo}
            open={dateToOpen}
            onOpenChange={setDateToOpen}
            onSelect={setDateTo}
          />

          <Select
            value={form.post_type}
            onValueChange={(value) =>
              setForm((prev) => ({
                ...prev,
                post_type: value ?? ALL_POST_TYPES,
              }))
            }
          >
            <SelectTrigger className="h-8 w-full">
              <span
                className={`truncate ${
                  form.post_type === ALL_POST_TYPES
                    ? "text-muted-foreground"
                    : ""
                }`}
              >
                {getPostTypeTriggerLabel(form.post_type)}
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_POST_TYPES}>Todos</SelectItem>
              {optionsType.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            name="post_code"
            placeholder="Número"
            className="h-8 w-full text-sm"
            value={form.post_code}
            maxLength={12}
            inputMode="numeric"
            pattern="[0-9]*"
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                post_code: e.target.value.replace(/\D/g, ""),
              }))
            }
          />

          <div className="relative w-full">
            <Input
              name="words"
              placeholder="Palavras-chave"
              className="h-8 w-full pr-9 text-sm"
              value={form.keywords}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, keywords: e.target.value }))
              }
            />
            <div className="pointer-events-auto absolute top-1/2 right-2 -translate-y-1/2">
              <InfoTooltip
                ariaLabel="Ajuda sobre palavras-chave"
                content={
                  <>
                    <p>
                      Separe as palavras com espaço no campo. Em &quot;Todas as
                      palavras&quot;, o resultado deve conter todas as palavras
                      indicadas.
                    </p>
                    <p className="mt-1">
                      Em &quot;Contém palavras&quot;, buscas parciais também são
                      consideradas (ex.: &quot;Palavra-Composta&quot;).
                    </p>
                  </>
                }
                triggerClassName="size-6"
              />
            </div>
          </div>

          <Select
            value={wordSearchMode}
            onValueChange={(value) =>
              setWordSearchMode((value as WordSearchMode) ?? "exact")
            }
          >
            <SelectTrigger className="h-8 w-full">
              <span className="truncate">
                {getWordSearchModeLabel(wordSearchMode)}
              </span>
            </SelectTrigger>
            <SelectContent>
              {WORD_SEARCH_MODES.map((mode) => (
                <SelectItem key={mode.value} value={mode.value}>
                  {mode.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default SearchComponents;
